/* eslint-disable max-lines */
import styled from '@emotion/styled';
import { Fonts, Spacings, mediaQuery } from '@styles/index';
import H4 from '@base/H4/H4';
import H5 from '@base/H5/H5';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import P2 from '@base/P2/P2';
import Button from '@base/Button/Button';
import Image from '@base/Image';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import { useRef, useState } from 'react';
import RadioList from '@comp/RadioList/RadioList';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';

import { segment } from '@actions/analytics';
import { DOW2_AAD_STARTED } from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useCustomDoubtsStore from '@stores/customDoubtsStore';
import {
  getLimitFlag,
  getLimitFlagSetter
} from '@stores/selectors/customDoubtSelectors';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import useDoubtConfig from '@api/hooks/goal/custom-doubts/useDoubtConfig';
import useSubjectAndLanguages from '@api/hooks/goal/custom-doubts/useSubjectAndLanguages';
import { saveLanguagePreference } from '@api/fetchers/doubts-and-solutions';
import { useRouter } from 'next/router';
import { getImage } from '@utils/imageHelper';
import LimitDialog from './LimitDialog';
import SampleDoubt from './SampleDoubts/SampleDoubts';
import ImageUploadDailog from './SubmitDoubt/ImageUploadDailog'; // incomplete
import ConfirmDoubtDetails from './SubmitDoubt/ConfirmDoubtDetails'; // incomplete
import 'cropperjs/dist/cropper.css';
import { getHeadingMessageImageUrl, showLimitFlag } from './utils/limitDailog';
import { dialogImage } from './DoubtSolution/CommonComponents';
import ImageSizeLimitDialog from './ImageSizeLimitDialog';
import DrawerFooter from './DrawerFooter';

const StyH4 = styled(H4)`
  margin-top: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_1B};
  grid-column: span 18;
  color: var(--color-text-primary);
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_4B};
  }
`;

const ImageUploadCard = styled.div`
  position: relative;
  margin-top: ${Spacings.SPACING_1B};
  grid-column: span 18;
  background-color: var(--color-base-1);
  border-radius: ${Spacings.SPACING_2B};
  box-shadow: ${boxShadowGenerator()};
  max-height: 210px;
  padding: ${Spacings.SPACING_8B};
  ${mediaQuery.uptoMobile} {
    max-height: 280px;
  }
`;

const CardHeading = styled(H5)``;

const UploadInstruction = styled(P2)`
  margin-top: ${Spacings.SPACING_2B};
  color: var(--color-text-secondary);
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    justify-content: space-evenly;
  }
`;

const UploadButton = styled(Button)`
  background-color: var(--color-base-fill);
  height: ${Spacings.SPACING_12B};
  padding: 0px ${Spacings.SPACING_8B};
  color: var(--color-text-tertiary);
  min-width: 277px;
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_2B};
    padding: 0px ${Spacings.SPACING_4B};
  }
`;

const SampleButton = styled(Button)`
  width: 136px;
  height: ${Spacings.SPACING_12B};
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
  margin-left: ${Spacings.SPACING_4B};
  font-weight: ${Fonts.BOLD};
  font-size: ${Fonts.SMALL};
  ${mediaQuery.uptoMobile} {
    margin-left: ${Spacings.SPACING_0B};
    width: 277px;
  }
`;

const SvgWrapper = styled(Image)`
  filter: invert(var(--filter));
`;

const ImageWrapper = styled.div`
  position: absolute;
  top: ${Spacings.SPACING_8B};
  right: ${Spacings.SPACING_8B};
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const UploadIcon = () => {
  return (
    <SvgWrapper
      src="doubts20/upload_image_camera.svg"
      alt="camera-icon"
      height={24}
      width={24}
    />
  );
};

const UploadImageOrTextIcon = () => {
  return (
    <SvgWrapper
      src="unacademy-ai/upload-image-text.svg"
      alt="camera-icon"
      height={40}
      width={40}
    />
  );
};

const StyInput = styled.input`
  display: none;
`;

const ErrorWrapper = styled.div`
  grid-column: span 10;
  display: flex;
  align-items: center;
  margin: ${Spacings.SPACING_4B} 0px;
`;

const UploadMsg = styled(P2)`
  margin-left: ${Spacings.SPACING_2B};
  color: var(--color-i-red);
`;

const UploadImage = styled.img``;

const UnacademyAICtaCard = ({ openLoginDrawer, isLoggedIn }) => {
  const router = useRouter();
  const { goalSlug, goalUID } = router.query;

  const routeGoalSegmentData = useGoalEventProps({ goalUID });

  const handleGoToUnacademyAi = () => {
    if (!isLoggedIn) {
      openLoginDrawer();
      return;
    }
    router.push(
      `/goal/${goalSlug}/${goalUID}/doubts-and-solutions/unacademy-ai`
    );
    segment.track(DOW2_AAD_STARTED, {
      last_primary_source_section: 'Menu Tab',
      click_a_picture_clicked: 'No',
      feature_version: 2,
      doubt_source: 'Doubts Page',
      doubt_type: 'Doubts AI',
      ...routeGoalSegmentData
    });
  };

  return (
    <ImageUploadCard>
      <CardHeading>Ask Unacademy AI</CardHeading>
      <UploadInstruction>
        Get instant solutions to your doubts using Unacademy AI
      </UploadInstruction>
      <ButtonsWrapper>
        <UploadButton
          grid=""
          icon={<UploadImageOrTextIcon />}
          variant="filled"
          color="black"
          label="Upload image or text question"
          onClick={handleGoToUnacademyAi}
        />
        <ImageWrapper>
          <UploadImage
            alt="upload-icon"
            src={getImage('unacademy-ai/unacademy-ai-intro.svg')}
            height={118}
            width={118}
          />
        </ImageWrapper>
      </ButtonsWrapper>
    </ImageUploadCard>
  );
};

const AadUploadImage = ({ goalUID, showDoubts, showAiDoubts }) => {
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const [isDrawerOpen, setIsDrawerOpened] = useState(false);
  const {
    doubtConfigData: {
      didUserAskFirstDoubt,
      dailyRemainingDoubtsCount,
      dailyTotalDoubtsCount,
      dailyRemainingOpenDoubtsCount,
      dailyTotalOpenDoubtsCount,
      maxAttachmentSizeInKB,
      isASDEnabled,
      preferredLanguage
    },
    mutate: refetchConfig
  } = useDoubtConfig(goalUID);
  const getImageSrc = () =>
    isDarkModeOn ? 'doubts20/uploadDoubt.svg' : 'doubts20/uploadDoubt.svg';

  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const [cropImgSrc, setCropImgSrc] = useState(null);
  const [isCropDialogOpen, setCropDialogOpen] = useState(null);
  const [rotate, setImageRotate] = useState(0);
  const [cropper, setCropper] = useState(null);
  const [fileObj, setFileObject] = useState(null);
  const [isUploading, setIsUploading] = useState(null);
  const [error, setError] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const hiddenFileInput = useRef(null);
  const routeGoalSegmentData = useGoalEventProps({ goalUID });
  const limitFlag = useCustomDoubtsStore(getLimitFlag);
  const setLimitFlag = useCustomDoubtsStore(getLimitFlagSetter);
  const [showImageSizeErrorDialog, setShowImageSizeErrorDialog] =
    useState(false);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const selectedDoubtLanguage = preferredLanguage;
  const [languageDrawer, setLanguageDrawer] = useState(false);
  const { subjectAndLanguageData } = useSubjectAndLanguages(goalUID);
  const { languages = [] } = subjectAndLanguageData;
  const [selectedRadio, setSelectedRadio] = useState(selectedDoubtLanguage?.id);

  const openLanguageDrawer = () => {
    if (isASDEnabled && (!selectedDoubtLanguage || !selectedDoubtLanguage.id)) {
      setLanguageDrawer(true);
      // eslint-disable-next-line no-useless-return
      return true;
    }
    return false;
  };

  const uploadHandleClick = () => {
    setUploadedImageUrl('');

    setError('');
    if (!isLoggedIn) {
      openLoginDrawer();
      return;
    }
    if (openLanguageDrawer()) return;
    if (
      showLimitFlag(dailyRemainingDoubtsCount, dailyRemainingOpenDoubtsCount)
    ) {
      setLimitFlag(true);
      return;
    }
    hiddenFileInput.current.click();
    segment.track(DOW2_AAD_STARTED, {
      last_primary_source_section: 'Menu Tab',
      click_a_picture_clicked: 'No',
      feature_version: 2,
      doubt_source: 'Doubts Page',
      doubt_type: 'AAD',
      ...routeGoalSegmentData
    });
  };

  const onCloseModal = () => {
    setLimitFlag(false);
    if (
      dailyRemainingDoubtsCount !== 0 &&
      dailyRemainingOpenDoubtsCount !== 0
    ) {
      hiddenFileInput.current.click();
    }
  };

  const onImageSizeErrorDailogClose = () => {
    setShowImageSizeErrorDialog(false);
  };

  const onChange = (e) => {
    e.preventDefault();
    let files;

    if (e.target.files.length > 0) {
      files = e.target.files;
    }
    const { size } = files[0];
    const imageSizeInKB = size / 1000;
    if (imageSizeInKB > maxAttachmentSizeInKB) {
      setShowImageSizeErrorDialog(true);
      e.target.value = '';
    } else {
      const reader = new FileReader();
      reader.onload = () => {
        setCropDialogOpen(true);
      };
      reader.readAsBinaryString(files[0]);
      setCropImgSrc(URL.createObjectURL(files[0]));
      setFileObject(files[0]);
      e.target.value = '';
    }
  };

  const { heading, message, imgSrc, msgWidth, statusCode } =
    getHeadingMessageImageUrl(
      dailyRemainingDoubtsCount,
      dailyRemainingOpenDoubtsCount,
      dailyTotalDoubtsCount,
      dailyTotalOpenDoubtsCount
    );

  const [errorMsg, setErrorMsg] = useState(null);
  const [sampleClicked, setSampleClicked] = useState(false);

  const onFooterNextClick = () => {
    if (!selectedRadio) {
      setErrorMsg('Select a language');
      return;
    }
    saveLanguagePreference(goalUID, selectedRadio).then((res) => {
      if (!res.error) {
        setLanguageDrawer(false);
        if (!sampleClicked) hiddenFileInput.current.click();
        refetchConfig();
      } else {
        setErrorMsg('Something went wrong.');
      }
    });
    if (sampleClicked) {
      setIsDrawerOpened(true);
    }
  };
  const getFooter = () => {
    return (
      <DrawerFooter
        errorMsg={errorMsg}
        onNextClick={onFooterNextClick}
        buttonText="Save"
        buttonWidth="136px"
      />
    );
  };
  const trySampleClicked = () => {
    setSampleClicked(true);
    if (!openLanguageDrawer()) setIsDrawerOpened(true);
  };

  if (!showAiDoubts && !showDoubts) {
    return null;
  }
  return (
    <>
      <RadioList
        isDrawerOpen={languageDrawer}
        mainHeading="Language preference"
        subHeading="Solution language preference for your doubts"
        list={languages.slice(1)}
        selectedItemUID={selectedRadio}
        onChange={setSelectedRadio}
        footer={getFooter()}
        needFooter
        openDrawer={setLanguageDrawer}
        color="var(--color-text-primary)"
      />
      <StyH4>Ask a new doubt</StyH4>
      {showAiDoubts ? (
        <>
          <UnacademyAICtaCard
            isLoggedIn={isLoggedIn}
            openLoginDrawer={openLoginDrawer}
          />
          <div style={{ height: '20px' }} />
        </>
      ) : null}
      {/* <OcrApp src={ocrURL} /> */}
      {showDoubts ? (
        <>
          <ImageUploadCard>
            <CardHeading>Upload a photo or a screenshot</CardHeading>
            <UploadInstruction>
              Photo should have printed text • Avoid blurry or dark photos • Use
              jpg or png format only
            </UploadInstruction>
            <ButtonsWrapper>
              <StyInput
                type="file"
                ref={hiddenFileInput}
                accept="image/png,image/jpeg"
                onChange={onChange}
              />
              <UploadButton
                grid=""
                icon={<UploadIcon />}
                variant="filled"
                color="black"
                label="Upload from your computer"
                width="277px"
                onClick={uploadHandleClick}
              />
              {didUserAskFirstDoubt === false ? (
                <SampleButton
                  width="136px"
                  variant="hollow"
                  label="Try sample"
                  color="black"
                  onClick={trySampleClicked}
                />
              ) : null}
            </ButtonsWrapper>
            {error && (
              <ErrorWrapper>
                <Image
                  src="oneToOneLiveMentoring/errorExclamation.svg"
                  width={Spacings.SPACING_6B}
                  height={Spacings.SPACING_6B}
                  alt="error"
                />
                <UploadMsg>{error}</UploadMsg>
              </ErrorWrapper>
            )}

            <ImageWrapper>
              <Image
                alt="upload-icon"
                src={getImageSrc()}
                height={118}
                width={118}
              />
            </ImageWrapper>
          </ImageUploadCard>
          {isDrawerOpen && (
            <SampleDoubt
              isDrawerOpen={isDrawerOpen}
              setIsDrawerOpened={setIsDrawerOpened}
              goalUID={goalUID}
              isASDEnabled={isASDEnabled}
              setSampleClicked={setSampleClicked}
            />
          )}

          <ImageUploadDailog
            isCropDialogOpen={isCropDialogOpen}
            rotate={rotate}
            fileObj={fileObj}
            isUploading={isUploading}
            setCropDialogOpen={setCropDialogOpen}
            setImageRotate={setImageRotate}
            setCropper={setCropper}
            setIsUploading={setIsUploading}
            cropper={cropper}
            // setCropData={setCropData}
            setUploadedImageUrl={setUploadedImageUrl}
            setError={setError}
            isASDEnabled={isASDEnabled}
            goalUID={goalUID}
            cropImgSrc={cropImgSrc}
          />
          {!isASDEnabled && uploadedImageUrl ? (
            <ConfirmDoubtDetails
              goalUID={goalUID}
              uploadedImageUrl={uploadedImageUrl}
              setUploadedImageUrl={setUploadedImageUrl}
              drawerState={uploadedImageUrl && !error}
            />
          ) : null}
          {limitFlag && (
            <LimitDialog
              heading={heading}
              message={message}
              imgComp={dialogImage(imgSrc)}
              statusCode={statusCode}
              onCloseModal={onCloseModal}
              msgWidth={msgWidth}
              goalUID={goalUID}
              askAnEducator={false}
            />
          )}
          {showImageSizeErrorDialog && (
            <ImageSizeLimitDialog
              maxAttachmentSizeInKB={maxAttachmentSizeInKB}
              dialogState={showImageSizeErrorDialog}
              onCloseDialog={onImageSizeErrorDailogClose}
              imgComp={dialogImage('doubts20/imageSizeUploadError.svg')}
            />
          )}
        </>
      ) : null}
    </>
  );
};
export default AadUploadImage;
